import {useMediaFrameElements} from '~/composables/studio/useMediaFrameElements'
import {useElementOffset} from '~/composables/dom/useElementOffset'
import {useElementSize} from '~/composables/dom/useElementSize'

const {
  baseStageCanvasControllerHeight,
  baseStageCanvasWidth,
  computeCanvasSizeProps,
  getCompositeCanvasHeight,
  getFrameDocument,
  getStageCanvas,
  getScaleRatio,
  overlayRelativeMargin,
} = useMediaFrameElements()

let mediaFrame: HTMLIFrameElement
let frameDocument: Document
let stageCanvas: HTMLCanvasElement

const hasAudio = ref<boolean>(false)
const hasVideo = ref<boolean>(false)

let userMediaStream: MediaStream
let userVideoWidget: HTMLDivElement
let userVideo: HTMLVideoElement
let userAudio: HTMLAudioElement

const webcamIsActive = ref<boolean>(false)
let webcamIsTopRight = ref<boolean>(false)
let webcamIsMaximized = ref<boolean>(false)

function hasRequiredFeatures(): boolean {
  if (!window.MediaRecorder) {
    return false
  }

  const c = document.createElement('canvas')
  return c.captureStream !== null
}

function checkUserMedia(): boolean {
  // @ts-ignore
  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    return true
  }

  // @ts-ignore
  const legacyUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia
  if (legacyUserMedia) {
    if (navigator.mediaDevices === undefined) {
      navigator.mediaDevices = {}
    }
    navigator.mediaDevices.getUserMedia = legacyUserMedia
    return true
  }

  return false
}

async function checkDevices() {
  if (navigator.mediaDevices.enumerateDevices) {
    const devices = await navigator.mediaDevices.enumerateDevices()
    devices.forEach(device => {
      if (device.kind === 'audioinput') {
        hasAudio.value = true
      } else if (device.kind === 'videoinput') {
        hasVideo.value = true
      }
    })
  }
}

async function initialize(_mediaFrame: HTMLIFrameElement): Promise<boolean> {
  mediaFrame = _mediaFrame

  const canvas = getStageCanvas(_mediaFrame)
  const doc = getFrameDocument(_mediaFrame)

  if (canvas && doc) {
    stageCanvas = canvas
    frameDocument = doc
  } else {
    throw new Error('No document and/or canvas')
  }

  if (!hasRequiredFeatures()) {
    throw new Error('Required features missing')
  }

  // Note: See https://stackoverflow.com/a/58449078/282695 for local dev

  if (!checkUserMedia()) {
    throw new Error('User media check failed')
  }

  // Add escape key listener?

  await checkDevices()

  if (!hasAudio.value) {
    throw new Error('No interesting stream available')
  }

  stopUserStream()
  await startUserStream()

  return true
}

function createWebcamWidget() {
  if (frameDocument.querySelector('.js-user-video-widget')) {
    return
  }

  userVideoWidget = document.createElement('div')
  userVideoWidget.classList.add('js-user-video-widget')

  userVideo = document.createElement('video')
  userVideo.classList.add('js-user-video')

  userVideo.muted = true
  userVideo.addEventListener('click', () => {
    webcamIsMaximized.value = !webcamIsMaximized.value
  })

  const cornerToggler = document.createElement('div')
  cornerToggler.classList.add('js-corner-toggler')
  cornerToggler.addEventListener('click', () => {
    webcamIsTopRight.value = !webcamIsTopRight.value
  })

  userVideoWidget.appendChild(userVideo)
  userVideoWidget.appendChild(cornerToggler)

  const videoWidgetStyles = document.createElement('style')
  videoWidgetStyles.innerHTML = `
.js-user-video-widget {
    position: absolute;
    overflow: hidden;
    opacity: 0;
    transition: opacity .25s, top .25s, left .25s, width .25s, height .25s;
}

.js-user-video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
}

.js-corner-toggler {
    position: absolute;
    width: 32px;
    height: 32px;
    line-height: 32px;
    background: #000000bf;
    color: #fff;
    text-align: center;
    opacity: 0;
    transition: opacity .25s,background .25s;
    cursor: pointer;
    top: 6px;
    right: 6px;
}

.js-user-video-widget:hover .js-corner-toggler {
    opacity: 1;
}

.js-corner-toggler:after {
    content: "";
    display: block;
    position: absolute;
    top: 6px;
    right: 6px;
    bottom: 6px;
    left: 6px;
    border: solid 4px #fff;
    border-left-width: 0;
    border-bottom-width: 0;
}

.js-user-video-widget.is-maximized .js-corner-toggler {
    opacity: 0;
}

.js-user-video-widget.is-top-right .js-corner-toggler {
    top: auto;
    right: auto;
    left: 6px;
    bottom: 6px;
}

.js-user-video-widget.is-top-right .js-corner-toggler:after {
    border: solid 4px #fff;
    border-right-width: 0;
    border-top-width: 0;
}
`

  frameDocument.head.insertAdjacentElement('beforeend', videoWidgetStyles)
  frameDocument.body.appendChild(userVideoWidget)

  watchEffect(() => {
    userVideoWidget.classList.remove('is-maximized', 'is-top-right')
    if (webcamIsMaximized.value) {
      userVideoWidget.classList.add('is-maximized')
    }
    if (webcamIsTopRight.value) {
      userVideoWidget.classList.add('is-top-right')
    }
  })

  onWebcamActiveChange()
}

function getWebcamPosition() {
  if (webcamIsMaximized.value) {
    return {
      top: 0,
      left: 0,
      width: useElementSize(stageCanvas).width,
      height: getCompositeCanvasHeight(stageCanvas, true),
    }
  }

  const stageCanvasOffset = useElementOffset(stageCanvas)
  const scaleRatio = getScaleRatio(stageCanvas, true)

  const webcamRelativeWidth = 100

  const webcamAbsoluteWidth = webcamRelativeWidth * scaleRatio
  const webcamAbsoluteMargin = overlayRelativeMargin * scaleRatio

  const {baseStageCanvasHeight} = computeCanvasSizeProps(stageCanvas)

  const webcamAbsoluteHeight = webcamAbsoluteWidth / (baseStageCanvasWidth / (baseStageCanvasHeight - baseStageCanvasControllerHeight))

  const topValue = webcamIsTopRight.value ? webcamAbsoluteMargin : (getCompositeCanvasHeight(stageCanvas, true) - webcamAbsoluteHeight - webcamAbsoluteMargin)
  const leftValue = webcamIsTopRight.value ? (stageCanvasOffset.left + useElementSize(stageCanvas).width - webcamAbsoluteWidth - webcamAbsoluteMargin) : (stageCanvasOffset.left + webcamAbsoluteMargin)

  return {
    top: topValue,
    left: leftValue,
    width: webcamAbsoluteWidth,
    height: webcamAbsoluteHeight,
  }
}

function placeUserVideoWidget() {
  const pos = getWebcamPosition()
  Object.keys(pos).forEach((positionProp: string) => {
    userVideoWidget.style.setProperty(positionProp, pos[positionProp as keyof typeof pos] + 'px')
  })
}

function onWebcamActiveChange() {
  if (!userVideoWidget) {
    return
  }

  userVideoWidget.style.opacity = webcamIsActive.value ? '1' : '0'
  window.removeEventListener('resize', placeUserVideoWidget)

  if (webcamIsActive.value) {
    placeUserVideoWidget()
    window.addEventListener('resize', placeUserVideoWidget)
  }
}

watch(webcamIsActive, onWebcamActiveChange)

watch([webcamIsMaximized, webcamIsTopRight], placeUserVideoWidget)

async function startUserStream() {
  if (hasVideo.value) {
    const stream: MediaStream | void = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    }).catch(e => {
      console.error(e)
    })

    if (!stream) {
      throw new Error('Refused audio and video stream')
    }

    userMediaStream = stream
    createWebcamWidget()
    userVideo.srcObject = stream
    await userVideo.play()
  } else {
    const stream: MediaStream | void = await navigator.mediaDevices.getUserMedia({audio: true}).catch(() => {
    })

    if (!stream) {
      throw new Error('Refused audio stream')
    }

    userMediaStream = stream
    if (!userAudio) {
      userAudio = document.createElement('audio')
      userAudio.muted = true
      userAudio.volume = 0
    }
    userAudio.srcObject = stream
    await userAudio.play()
  }
}

function stopUserStream() {
  userMediaStream?.getTracks().forEach((track: MediaStreamTrack) => {
    track.stop()
  })
}

export const useUserVideoOverlay = () => {
  return {
    initialize,
    hasAudio,
    hasVideo,
    webcamIsActive,
    webcamIsMaximized,
    webcamIsTopRight,
    userMediaStream,
    stopUserStream,
  }
}
